import PropTypes from 'prop-types';
import React from 'react';
import { Translate } from 'react-localize-redux';
import { useSelector } from 'react-redux';
import Icon, { IconSizes, IconVariants } from '../../components/foundations/Icon';
import { ExpandablePanel, GroupContainer } from '../../components/widgets';
import { TOGGLE_TAB_GROUP_OPTIONS } from '../consts';
import { getSelectedToggleTab } from '../selectors';
import css from './LinkGroupContainers.css';
import LinkGroupHeader from './LinkGroupHeader';
import LinkList from './LinkList';

function LinkGroupContainers({
    groups,
    onTitleClick,
    handleArchiveLink,
    onEditClick,
    onViewClick,
    onDuplicateClicked,
    onExpandClick,
    isGroupOpen,
}) {
    const selectedToggleTab = useSelector(state => getSelectedToggleTab(state));

    const renderHeader = partner => {
        const countObjects = [
            {
                translateIdSuffix: 'LINK_GROUP_HEADER_SUBTITLE',
                count: partner.links.filter(link => !link.agency_name && link.singular_link && !link.is_api_link)
                    .length,
                show: [TOGGLE_TAB_GROUP_OPTIONS.ALL_LINKS, TOGGLE_TAB_GROUP_OPTIONS.STANDARD_LINKS].includes(
                    selectedToggleTab
                ),
            },
            {
                translateIdSuffix: 'API_LINKS_GROUP_HEADER_SUBTITLE',
                count: partner.links.filter(link => link.is_api_link).length,
                show: TOGGLE_TAB_GROUP_OPTIONS.API_LINKS === selectedToggleTab,
            },
            {
                translateIdSuffix: 'AGENCY_LINKS_GROUP_HEADER_SUBTITLE',
                count: partner.links.filter(link => link.agency_name).length,
                show: [TOGGLE_TAB_GROUP_OPTIONS.AGENCY_LINKS, TOGGLE_TAB_GROUP_OPTIONS.ALL_LINKS].includes(
                    selectedToggleTab
                ),
                iconName: 'briefcase',
            },
            {
                translateIdSuffix: 'OLD_LINKS_GROUP_HEADER_SUBTITLE',
                count: partner.links.filter(link => !link.singular_link).length,
                show: [TOGGLE_TAB_GROUP_OPTIONS.OLD_LINKS, TOGGLE_TAB_GROUP_OPTIONS.ALL_LINKS].includes(
                    selectedToggleTab
                ),
                iconName: 'history-toggle',
            },
        ];
        const filteredCountObjects = countObjects.filter(countObject => countObject.show && countObject.count > 0);

        return (
            <LinkGroupHeader
                {...partner}
                title={partner.name}
                subTitle={
                    <div className={css.subTitle}>
                        {filteredCountObjects.map(({ iconName, translateIdSuffix, count }, i) => (
                            <React.Fragment key={translateIdSuffix}>
                                {i > 0 && <hr className={css.divider} />}
                                {iconName && (
                                    <Icon
                                        name={iconName}
                                        size={IconSizes.X_SMALL}
                                        variant={IconVariants.LIGHT}
                                        interactive={false}
                                    />
                                )}
                                <Translate id={`STATIC.PAGES.MANAGE_LINKS.${translateIdSuffix}`} data={{ count }} />
                            </React.Fragment>
                        ))}
                    </div>
                }
            />
        );
    };

    return groups.map(group => (
        <GroupContainer key={group.name} {...group} controlled onTitleClick={onTitleClick} open={isGroupOpen(group)}>
            {group.partners.map((partner, index) => (
                <div
                    key={`${selectedToggleTab}-${partner.name}`}
                    className={css.expandableOverrideContainer}
                    style={{ '--stagger-delay': `${Math.min(1, index * 0.05)}s` }}
                >
                    <ExpandablePanel
                        id={partner.name}
                        expanded={partner.expanded}
                        content={
                            <LinkList
                                links={partner.links}
                                onEditClick={onEditClick}
                                onViewClick={onViewClick}
                                onDuplicateClicked={onDuplicateClicked}
                                onArchiveClicked={handleArchiveLink}
                            />
                        }
                        header={renderHeader(partner)}
                        overrideClasses={{
                            container: css.expandableOverrideContainer,
                            contentArea: css.expandableOverrideContent,
                            headerContainer: css.expandableOverrideHeader,
                            headerContainerExpanded: css.expandableOverrideHeaderExpanded,
                            arrowArea: css.expandableOverrideArrow,
                            externalHeaderArea: css.expandableOverrideExternal,
                        }}
                        onExpandClick={onExpandClick}
                        animate={false}
                    />
                </div>
            ))}
        </GroupContainer>
    ));
}

LinkGroupContainers.propTypes = {
    groups: PropTypes.arrayOf(PropTypes.any).isRequired,
    onTitleClick: PropTypes.func.isRequired,
    handleArchiveLink: PropTypes.func.isRequired,
    onEditClick: PropTypes.func.isRequired,
    onViewClick: PropTypes.func.isRequired,
    onDuplicateClicked: PropTypes.func.isRequired,
    onExpandClick: PropTypes.func.isRequired,
    isGroupOpen: PropTypes.func.isRequired,
};

export default LinkGroupContainers;
